import { Injectable, inject, signal } from '@angular/core';
import type { AppPlugin } from '@capacitor/app';
import type { HttpOptions, HttpResponse } from '@capacitor/core';
import type { BundleInfo, CapacitorUpdaterPlugin } from '@capgo/capacitor-updater';

import { isCapacitor } from '@shared/utils/platform.utils';
import { compareSemver } from '@shared/utils/version.utils';
import { environment } from 'src/environments/environment';
import { ROLLBAR } from './rollbar.service';
import { ToastService } from './toast.service';

/**
 * Superficie mínima de `CapacitorHttp` que este servicio necesita. Se declara aquí (en vez de
 * importar el tipo completo del plugin) para que el spec pueda falsearlo sin arrastrar el
 * paquete nativo.
 */
export interface MobileHttpPlugin {
  get(options: HttpOptions): Promise<HttpResponse>;
}

/** Forma del manifest estático que publica el workflow de release en R2 (`mobile/latest.json`). */
export interface MobileUpdateManifest {
  /** Versión (semver) del bundle web publicado. */
  version: string;
  /** URL del zip del bundle (`dist/app-erp/browser`) a descargar. */
  url: string;
  /** Checksum sha256 del zip, para integridad (ver `README` del plugin, `DownloadOptions.checksum`). */
  checksum?: string;
  /**
   * Versión mínima del SHELL NATIVO (APK) requerida para que este bundle web funcione.
   * Si la app instalada es más vieja, no se aplica el update: se avisa al usuario que
   * reinstale (un cambio nativo, p.ej. un plugin nuevo, no se puede resolver con un OTA de JS).
   */
  minNativeVersion?: string;
}

/**
 * URL fija del manifest de auto-update móvil (mismo patrón que `latest.json` de Tauri,
 * ver `src-tauri/tauri.conf.json` y `docs/modules/DESKTOP.md`): bucket R2 público
 * `kerep-updates`, publicado por `.github/workflows/mobile-release.yml`.
 *
 * NOTA: para probar el loop completo contra un servidor local (emulador Android) se apunta
 * esta constante temporalmente a `http://10.0.2.2:<puerto>/latest.json` y se revierte antes
 * de terminar — nunca debe quedar commiteado apuntando a otro sitio que no sea R2.
 */
export const MOBILE_UPDATE_MANIFEST_URL = 'https://updates.kerep.app/mobile/latest.json';

/**
 * Auto-update self-hosted del bundle WEB embebido en la app Android (equivalente móvil del
 * updater de Tauri, ver `src/main.ts`), usando `@capgo/capacitor-updater` en modo **"No Cloud"**
 * (`autoUpdate: false` en `capacitor.config.ts`): el chequeo de versión, la descarga y la
 * activación del bundle las controla este servicio a mano contra un manifest JSON **estático**
 * (`MOBILE_UPDATE_MANIFEST_URL`), no contra el `updateUrl` automático del plugin (ese endpoint
 * habla el protocolo propio — y de pago — de la nube de Capgo, no sirve un archivo estático).
 *
 * ## Flujo interactivo (v2)
 *
 * A diferencia de la v1 (silenciosa con `next()`), el flujo actual es **interactivo y
 * transparente**:
 *
 * 1. Al arrancar, `init()` consulta el manifest de R2 y, si hay versión nueva, marca
 *    `updateAvailable` y `availableVersion` — no descarga nada aún. El chequeo se repite al
 *    volver la app a primer plano y cada `recheckIntervalMs` (ver "Cadencia del chequeo").
 * 2. El `AppComponent` observa `updateAvailable` y muestra un toast prioritario con el
 *    botón "Actualizar Ahora".
 * 3. Al tocar el botón, `applyUpdate()` descarga el bundle con barra de progreso en vivo
 *    (`downloadProgress` alimentado por el evento `download` del plugin) y, al completar,
 *    lo aplica **de inmediato** con `set()`, que recarga el WebView al instante.
 * 4. Si algo falla, `applyUpdate()` devuelve `false` y deja `updateAvailable` en `true`: el
 *    `AppComponent` vuelve a mostrar el toast para que el usuario pueda reintentar sin
 *    reiniciar la app.
 *
 * ## Seguridad de rollback
 *
 * `notifyAppReady()` se llama SIEMPRE en cada arranque nativo, incluso con el updater
 * desactivado (`environment.mobileUpdaterEnabled === false`): si una sesión previa dejó
 * aplicado un bundle nuevo, el plugin necesita esta confirmación en cada arranque o lo
 * revierte automáticamente al último bundle que sí confirmó.
 *
 * ## Cadencia del chequeo
 *
 * El arranque en frío no basta: en Android moderno el proceso casi nunca muere, así que una app
 * que se usa a diario puede pasar semanas sin un arranque real. Por eso el chequeo se dispara en
 * tres momentos: al arrancar (`init()`), cada vez que la app vuelve a primer plano
 * (`appStateChange`) y periódicamente mientras siga en primer plano (`recheckIntervalMs`, para
 * la terminal que queda abierta todo el día). Un `recheckThrottleMs` evita golpear R2 en cada
 * cambio de app, y el re-chequeo se salta si ya hay un aviso publicado o una descarga en curso.
 *
 * ## Por qué el manifest se pide por el puente NATIVO
 *
 * `fetchManifest()` usa `CapacitorHttp` (petición hecha en Java), no el `fetch()` del WebView:
 * el WebView corre en el origen `https://localhost` (`androidScheme: 'https'`), así que un
 * `fetch()` a `updates.kerep.app` es **cross-origin** y muere si el bucket no publica cabeceras
 * CORS. Eso fue exactamente el bug del 2026-08-13: el updater nunca avisó de ninguna versión
 * porque el `fetch` fallaba con `TypeError: Failed to fetch` y el error moría en un `console.warn`
 * (que además, en un APK release, Capacitor ni siquiera reenvía a logcat). Por el puente nativo
 * no hay política de mismo origen que aplicar — es la misma razón por la que el updater de Tauri,
 * que hace su HTTP en Rust, sí funcionaba. **No lo "simplifiques" de vuelta a `fetch()`.**
 *
 * ## Testabilidad
 *
 * Igual que `MobileShellService`, los `import()` dinámicos de los plugins están detrás de
 * métodos `protected` sobreescribibles en los specs (no `vi.mock`).
 */
@Injectable({ providedIn: 'root' })
export class MobileUpdaterService {
  private readonly toast = inject(ToastService);
  /**
   * Opcional a propósito: el token solo está provisto en la app real (`app.config.ts`), no en
   * los specs que construyen el servicio a mano.
   */
  private readonly rollbar = inject(ROLLBAR, { optional: true });

  // ── Señales reactivas consumibles por la UI ──────────────────────────────

  /**
   * `true` cuando el shell nativo instalado (APK) queda por debajo del `minNativeVersion` del
   * último manifest visto. Consumible por el layout para mostrar un aviso persistente (v1: un
   * toast fijo disparado por este mismo servicio; ver `checkMinNativeVersion`).
   */
  readonly nativeUpdateRequired = signal(false);

  /** Hay una versión nueva detectada y lista para que el usuario inicie la descarga. */
  readonly updateAvailable = signal(false);

  /** Versión semver del bundle disponible en R2 (p. ej. `'0.119.0'`). */
  readonly availableVersion = signal('');

  /** Descarga del bundle en curso (el usuario ya tocó "Actualizar Ahora"). */
  readonly downloading = signal(false);

  /** Porcentaje de descarga del bundle (0–100). Se actualiza con el evento `download` del plugin. */
  readonly downloadProgress = signal(0);

  /** Se está aplicando el bundle descargado (entre `download` completado y `set()`). */
  readonly applying = signal(false);

  /**
   * Mensaje del último chequeo de manifest fallido (`''` si el último fue correcto). El fallo no
   * se le muestra al usuario —quedarse sin red es normal y un toast por arranque sería ruido—,
   * pero deja rastro consultable en vez de morir en la consola.
   */
  readonly lastCheckError = signal('');

  // ── Estado privado ───────────────────────────────────────────────────────

  /** Manifest almacenado para usar en `applyUpdate()` después de que el usuario acepte. */
  private pendingManifest: MobileUpdateManifest | null = null;

  /** Marca temporal del último chequeo (correcto o fallido), para el throttle del re-chequeo. */
  private lastCheckAt = 0;

  /** Chequeo en curso: dos disparos simultáneos (resume + temporizador) comparten el mismo. */
  private inFlightCheck: Promise<void> | null = null;

  /** Temporizador del chequeo periódico en primer plano. */
  private recheckTimer: ReturnType<typeof setInterval> | null = null;

  /** Un fallo de chequeo se reporta una sola vez por sesión (no por cada reintento). */
  private checkFailureReported = false;

  /**
   * Tiempo mínimo entre dos chequeos de manifest. Sin él, alternar de app repetidamente
   * dispararía una petición a R2 por cada vuelta a primer plano.
   * `protected` (no `readonly`) para poder ajustarlo en los specs.
   */
  protected recheckThrottleMs: number = 30 * 60_000;

  /**
   * Cadencia del chequeo mientras la app permanece en primer plano. Cubre el caso de la terminal
   * que nadie cierra ni manda a segundo plano en todo el día.
   */
  protected recheckIntervalMs: number = 30 * 60_000;

  /** Temporizador de seguridad que desbloquea el diálogo modal si `set()` no recarga el WebView. */
  private applyWatchdog: ReturnType<typeof setTimeout> | null = null;

  /**
   * Margen para que `set()` recargue el WebView antes de dar el intento por fallido. El diálogo
   * de progreso es modal y no cerrable: sin este watchdog, un `set()` que resuelva sin recargar
   * (o que se cuelgue) deja al usuario con la app bloqueada y sin salida.
   * `protected` para poder acortarlo en los specs.
   */
  protected readonly applyWatchdogMs: number = 15_000;

  async init(): Promise<void> {
    if (!isCapacitor()) return;

    await this.notifyAppReady();

    if (!environment.mobileUpdaterEnabled) {
      console.info('MobileUpdater: desactivado por configuración (NG_APP_MOBILE_UPDATER).');
      return;
    }

    await this.runCheck();
    await this.watchAppState();
    this.startPeriodicCheck();
  }

  /**
   * Re-chequeo oportunista del manifest (vuelta a primer plano y temporizador periódico).
   * Se salta a sí mismo si el updater está apagado, si ya hay un aviso publicado, si hay una
   * descarga/aplicación en curso o si el último chequeo fue hace menos de `recheckThrottleMs`.
   */
  async recheck(): Promise<void> {
    if (!isCapacitor() || !environment.mobileUpdaterEnabled) return;
    if (this.updateAvailable() || this.downloading() || this.applying()) return;
    if (Date.now() - this.lastCheckAt < this.recheckThrottleMs) return;

    await this.runCheck();
  }

  /** Detiene el chequeo periódico. Lo usan los specs; en la app el servicio vive todo el proceso. */
  stopPeriodicCheck(): void {
    if (this.recheckTimer === null) return;
    clearInterval(this.recheckTimer);
    this.recheckTimer = null;
  }

  /**
   * Descarga el bundle pendiente con barra de progreso y lo aplica de inmediato con `set()`.
   * Llamado por el botón "Actualizar Ahora" del toast en `AppComponent`.
   *
   * ADVERTENCIA: `set()` destruye el contexto de JS activo (recarga el WebView). Cualquier
   * código después de `set()` no se ejecutará (salvo el `finally`, que corre antes de que el
   * puente nativo devuelva el control).
   *
   * @returns `false` si no había nada pendiente o si el intento falló — en ese caso
   * `updateAvailable` sigue en `true` y el llamador debe volver a ofrecer el reintento.
   */
  async applyUpdate(): Promise<boolean> {
    if (!this.pendingManifest) return false;

    const manifest = this.pendingManifest;
    this.downloading.set(true);
    this.downloadProgress.set(0);

    // Un reintento tras un fallo no debe acumular listeners del evento `download`.
    let removeDownloadListener: (() => Promise<void>) | null = null;

    try {
      const { plugin: updater } = await this.loadUpdaterPlugin();

      // Registrar listener de progreso ANTES de iniciar la descarga.
      removeDownloadListener = await this.addDownloadListener(updater);

      const downloaded: BundleInfo = await updater.download({
        version: manifest.version,
        url: manifest.url,
        checksum: manifest.checksum,
      });

      this.downloading.set(false);
      this.applying.set(true);
      this.armApplyWatchdog();

      // `set()` recarga el WebView de inmediato con el bundle descargado.
      await updater.set({ id: downloaded.id });
      return true;
    } catch (err) {
      console.error('MobileUpdater: error al descargar/aplicar la actualización.', err);
      this.clearApplyWatchdog();
      this.downloading.set(false);
      this.applying.set(false);
      this.toast.error('No se pudo descargar la actualización. Intenta de nuevo más tarde.', {
        summary: 'Error de actualización',
      });
      return false;
    } finally {
      try {
        await removeDownloadListener?.();
      } catch (err) {
        console.warn('MobileUpdater: no se pudo remover el listener de descarga.', err);
      }
    }
  }

  // ── Puntos de carga dinámica (sobreescribibles en tests) ─────────────────

  /**
   * Punto de carga dinámica del plugin `@capgo/capacitor-updater` (sobreescribible en tests).
   *
   * `CapacitorUpdater` es un *proxy* del puente nativo: cualquier acceso a una propiedad
   * (incluido `then`) dispara una llamada nativa. Si esta función `async` resolviera el
   * proxy directamente, el motor de JS comprobaría "es thenable" leyendo
   * `CapacitorUpdater.then()` → error nativo `"CapacitorUpdater.then() is not implemented"`
   * y la promesa queda colgada para siempre. Por eso el proxy viaja SIEMPRE envuelto en un
   * objeto plano (`{ plugin }`).
   */
  protected async loadUpdaterPlugin(): Promise<{ plugin: CapacitorUpdaterPlugin }> {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    return { plugin: CapacitorUpdater };
  }

  /**
   * Punto de carga dinámica del plugin `@capacitor/app` (sobreescribible en tests).
   * Mismo motivo que `loadUpdaterPlugin`: el proxy nativo no puede ser el valor resuelto
   * de una promesa.
   */
  protected async loadAppPlugin(): Promise<{ plugin: AppPlugin }> {
    const { App } = await import('@capacitor/app');
    return { plugin: App };
  }

  /**
   * Punto de carga dinámica de `CapacitorHttp` (sobreescribible en tests).
   * Mismo contrato `{ plugin }` que los demás loaders: es un proxy del puente nativo.
   */
  protected async loadHttpPlugin(): Promise<{ plugin: MobileHttpPlugin }> {
    const { CapacitorHttp } = await import('@capacitor/core');
    return { plugin: CapacitorHttp as MobileHttpPlugin };
  }

  /**
   * Descarga del manifest estático por el puente NATIVO (sobreescribible en tests).
   *
   * Ver "Por qué el manifest se pide por el puente NATIVO" en la cabecera de la clase: con el
   * `fetch()` del WebView la petición es cross-origin y depende de que el bucket publique CORS.
   * `Cache-Control: no-cache` mantiene la intención del antiguo `cache: 'no-store'`: nunca servir
   * un manifest viejo.
   */
  protected async fetchManifest(): Promise<MobileUpdateManifest> {
    const { plugin: http } = await this.loadHttpPlugin();
    const res = await http.get({
      url: MOBILE_UPDATE_MANIFEST_URL,
      headers: { 'Cache-Control': 'no-cache' },
      responseType: 'json',
    });

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`MobileUpdater: manifest respondió HTTP ${res.status}`);
    }

    // El puente devuelve el cuerpo ya parseado cuando el `Content-Type` es JSON, pero no es
    // garantía (R2 puede servir el .json como texto plano): aceptamos ambas formas.
    return (typeof res.data === 'string' ? JSON.parse(res.data) : res.data) as MobileUpdateManifest;
  }

  /**
   * Registra el listener del evento `download` del plugin para actualizar `downloadProgress`.
   * Sobreescribible en tests para evitar importar el plugin nativo.
   *
   * @returns La función para removerlo; `applyUpdate()` la invoca al terminar el intento para
   * que un reintento no deje listeners duplicados alimentando `downloadProgress`.
   */
  protected async addDownloadListener(updater: CapacitorUpdaterPlugin): Promise<() => Promise<void>> {
    const handle = await updater.addListener('download', (event: { percent: number }) => {
      this.downloadProgress.set(event.percent);
    });
    return () => handle.remove();
  }

  // ── Métodos privados ─────────────────────────────────────────────────────

  /**
   * Arma el temporizador de seguridad del paso de aplicación. Si `set()` recarga el WebView
   * (camino normal) el temporizador muere con el contexto de JS; si no, libera el diálogo
   * modal y avisa al usuario en vez de dejarlo bloqueado.
   */
  private armApplyWatchdog(): void {
    this.clearApplyWatchdog();
    this.applyWatchdog = setTimeout(() => {
      if (!this.applying()) return;
      console.error('MobileUpdater: set() no recargó el WebView dentro del margen esperado.');
      this.applying.set(false);
      this.toast.error('No se pudo aplicar la actualización. Cierra y vuelve a abrir la app.', {
        summary: 'Error de actualización',
      });
    }, this.applyWatchdogMs);
  }

  private clearApplyWatchdog(): void {
    if (this.applyWatchdog === null) return;
    clearTimeout(this.applyWatchdog);
    this.applyWatchdog = null;
  }

  /**
   * Ejecuta un chequeo de manifest sin throttle, tolerando cualquier fallo: un manifest caído o
   * un teléfono sin red nunca debe romper el arranque ni la sesión. Si ya hay uno en curso
   * (resume y temporizador pueden coincidir), se comparte en vez de duplicar la petición.
   */
  private runCheck(): Promise<void> {
    if (this.inFlightCheck) return this.inFlightCheck;

    this.inFlightCheck = this.checkForUpdate()
      .then(() => {
        this.lastCheckError.set('');
      })
      .catch((err: unknown) => {
        this.registerCheckFailure(err);
      })
      .finally(() => {
        this.lastCheckAt = Date.now();
        this.inFlightCheck = null;
      });

    return this.inFlightCheck;
  }

  /**
   * Deja rastro de un chequeo fallido. Distingue dos mundos:
   *
   * - **Sin conexión**: fallo esperado y transitorio. Solo se anota en la señal; reportarlo
   *   saturaría Rollbar con cada usuario que entre a un sótano.
   * - **Con conexión**: manifest caído, CORS ausente, JSON inválido… es un bug de despliegue que
   *   deja el canal de updates muerto **en silencio** (en un APK release Capacitor no reenvía
   *   `console.*` a logcat, así que no hay forma de verlo desde el dispositivo). Se reporta una
   *   vez por sesión. Esto es lo que habría delatado el incidente del 2026-08-13 el primer día.
   */
  private registerCheckFailure(err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.lastCheckError.set(message);
    console.error('[MobileUpdater] No se pudo comprobar actualizaciones.', err);

    if (!navigator.onLine || this.checkFailureReported) return;
    this.checkFailureReported = true;
    this.rollbar?.warning('MobileUpdater: no se pudo comprobar actualizaciones', {
      manifestUrl: MOBILE_UPDATE_MANIFEST_URL,
      error: message,
    });
  }

  /** Re-chequea cada vez que Android devuelve la app a primer plano. */
  private async watchAppState(): Promise<void> {
    try {
      const { plugin: App } = await this.loadAppPlugin();
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void this.recheck();
      });
    } catch (err) {
      console.warn('[MobileUpdater] No se pudo observar el ciclo de vida de la app.', err);
    }
  }

  /** Chequeo periódico para sesiones que nunca pasan por segundo plano. */
  private startPeriodicCheck(): void {
    if (this.recheckTimer !== null) return;
    this.recheckTimer = setInterval(() => void this.recheck(), this.recheckIntervalMs);
  }

  private async notifyAppReady(): Promise<void> {
    try {
      const { plugin: updater } = await this.loadUpdaterPlugin();
      await updater.notifyAppReady();
    } catch (err) {
      console.warn('MobileUpdater: no se pudo notificar appReady (posible rollback).', err);
    }
  }

  private async checkForUpdate(): Promise<void> {
    const manifest = await this.fetchManifest();

    await this.checkMinNativeVersion(manifest.minNativeVersion);

    const { plugin: updater } = await this.loadUpdaterPlugin();
    const { bundle, native } = await updater.current();
    // Bundle "builtin" = el que trae el APK de fábrica; su versión es la del shell nativo.
    const currentVersion = bundle.id === 'builtin' ? native : bundle.version;

    if (compareSemver(manifest.version, currentVersion) <= 0) {
      return; // Ya estamos en la última versión (o en una más nueva que la publicada).
    }

    // Hay versión nueva: guardar el manifest y avisar a la UI. No descargamos aún: el
    // usuario decide cuándo con el botón "Actualizar Ahora".
    this.pendingManifest = manifest;
    this.availableVersion.set(manifest.version);
    this.updateAvailable.set(true);
  }

  private async checkMinNativeVersion(minNativeVersion: string | undefined): Promise<void> {
    if (!minNativeVersion) return;
    try {
      const { plugin: App } = await this.loadAppPlugin();
      const info = await App.getInfo();
      if (compareSemver(info.version, minNativeVersion) < 0) {
        this.nativeUpdateRequired.set(true);
        // v1: toast fijo (life: 0, requiere cierre manual) en vez de un banner dedicado —
        // patrón aceptado explícitamente para esta fase (ver spec de la tarea F2-T7).
        this.toast.warn(
          'Hay una versión nueva de la app que requiere reinstalarla. Algunas funciones podrían no estar disponibles hasta entonces.',
          { summary: 'Actualización requerida', life: 0 }
        );
      }
    } catch (err) {
      console.warn('MobileUpdater: no se pudo verificar la versión nativa mínima.', err);
    }
  }
}
